// components/invoices/SubmissionProgressModal.tsx

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Stack,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Autorenew as LoadingIcon
} from '@mui/icons-material';

interface ProgressStep {
  label: string;
  status: 'pending' | 'loading' | 'complete' | 'error';
  detail?: string;
}

interface SubmissionProgressModalProps {
  open: boolean;
  steps: ProgressStep[];
  currentStep: number;
  error: string | null;
  onClose?: () => void;
}

export const SubmissionProgressModal: React.FC<SubmissionProgressModalProps> = ({
  open,
  steps,
  currentStep,
  error,
  onClose
}) => {
  const allComplete = steps.length > 0 && steps.every(step => step.status === 'complete');
  const hasError = error !== null;

  const getStepIcon = (status: ProgressStep['status']) => {
    switch (status) {
      case 'complete':
        return <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'loading':
        return <LoadingIcon sx={{ color: 'primary.main', fontSize: 20, animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      default:
        return <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'grey.300' }} />;
    }
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={!hasError && !allComplete}
    >
      <DialogTitle>
        {hasError ? 'Submission Failed' : allComplete ? 'Invoice Submitted Successfully!' : 'Submitting Invoice...'}
      </DialogTitle>
      
      <DialogContent>
        {hasError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : !allComplete && (
          <LinearProgress sx={{ mb: 3 }} />
        )}

        <Stack spacing={2}>
          {steps.map((step, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                opacity: step.status === 'pending' ? 0.5 : 1,
                transition: 'opacity 0.3s'
              }}
            >
              {getStepIcon(step.status)}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: step.status === 'loading' ? 600 : 400,
                    color: step.status === 'loading' ? 'primary.main' : 'text.primary'
                  }}
                >
                  {step.label}
                </Typography>
                {step.detail && (
                  <Typography variant="caption" color="text.secondary">
                    {step.detail}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Stack>

        {allComplete && !hasError && (
          <Alert severity="success" sx={{ mt: 3 }}>
            Your invoice has been successfully submitted and sent to Monday.com.
          </Alert>
        )}
      </DialogContent>

      {(hasError || allComplete) && (
        <DialogActions>
          <Button onClick={onClose} variant="contained" color="primary">
            {hasError ? 'Close' : 'Go to Invoice List'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};